import React from 'react';
import { Bot, CheckCircle, ExternalLink, Lightbulb, Copy, Info } from 'lucide-react';

export default function AIAssistantPanel({ data, loading, onResolve, aiSuggestion, onGetFix, onDiagnose, onImprove }) {
  if (!data && !loading && !aiSuggestion) {
    return (
      <div className="card ai-panel-empty ai-panel-accented" style={{ background: 'rgba(96, 165, 250, 0.05)', border: '1px solid rgba(96, 165, 250, 0.2)', borderRadius: 16, overflow: 'hidden', backdropFilter: 'blur(16px)' }}>
        <div className="ai-panel-header" style={{ padding: '16px 20px', borderBottom: '1px solid rgba(96, 165, 250, 0.1)', background: 'rgba(96, 165, 250, 0.05)' }}>
          <div className="ai-header-main" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, fontWeight: 800, color: 'var(--text-60a5fa)' }}>
            <Bot size={20} />
            AI Assistant
          </div>
          <div className="ai-header-sub" style={{ fontSize: 11, color: 'rgba(96, 165, 250, 0.6)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>Powered by BEO IT</div>
        </div>
        <div className="p-5">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
            <button onClick={onGetFix} disabled={loading} style={{ height: 42, borderRadius: 10, background: 'var(--text-60a5fa)', color: '#fff', border: 'none', fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 15px rgba(96, 165, 250, 0.3)' }}>Get Fix</button>
            <button onClick={onDiagnose} disabled={loading} style={{ height: 42, borderRadius: 10, background: 'var(--white-05)', color: 'var(--text-primary)', border: '1px solid var(--white-06)', fontWeight: 700, cursor: 'pointer' }}>Diagnose Issue</button>
            <button onClick={onImprove} disabled={loading} style={{ height: 42, borderRadius: 10, background: 'var(--white-05)', color: 'var(--text-primary)', border: '1px solid var(--white-06)', fontWeight: 700, cursor: 'pointer' }}>{loading ? 'Improving...' : 'Improve Description'}</button>
          </div>
          <div className="text-center" style={{ color: 'var(--white-40)', fontSize: 13, lineHeight: 1.5 }}>
            Describe your issue to get tailored AI solutions instantly.
          </div>
        </div>
      </div>
    );
  }

  const renderRealTimeSuggestion = () => {
    if (!aiSuggestion || data || loading) return null;
    return (
      <div className="ai-section animate-fade-in ai-panel-accented" style={{ background: 'rgba(96, 165, 250, 0.08)', padding: '16px', borderRadius: '12px', border: '1px solid rgba(96, 165, 250, 0.2)' }}>
        <h4 className="ai-section-title" style={{ color: 'var(--text-60a5fa)', fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', marginBottom: '10px', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Bot size={14} /> Real-time Insight
        </h4>
        <div className="ai-text" style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-primary)' }}>
          This looks like a <span style={{ color: 'var(--text-60a5fa)' }}>{aiSuggestion.category}</span> issue.
        </div>
        <p style={{ fontSize: 12, color: 'var(--white-50)', marginTop: 10, lineHeight: 1.5 }}>
          {aiSuggestion.category === 'Access' && "Tip: Check if your account is locked in LDAP first."}
          {aiSuggestion.category === 'Network' && "Tip: Restart your VPN client or check Wi-Fi signal."}
          {aiSuggestion.category === 'Hardware' && "Tip: Ensure all cables are plugged in and device is powered on."}
          {aiSuggestion.category === 'Software' && "Tip: Try clearing your browser cache or restarting the app."}
        </p>
      </div>
    );
  };

  return (
    <div className={`card ai-panel ai-panel-accented ${loading ? 'ai-loading' : ''}`}>
      <div className="ai-panel-header">
        <div className="ai-header-main">
          <Bot size={18} className="text-ai-brand" style={{ color: 'var(--text-60a5fa)' }} />
          AI Assistant
        </div>
        <div className="ai-header-sub">Powered by BEO IT</div>
        {loading && <div className="spinner-sm ml-auto"></div>}
      </div>

      <div className="ai-panel-body">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
          <button onClick={onGetFix} disabled={loading} className="btn btn-primary btn-sm" style={{ flex: 1 }}>Get Fix</button>
          <button onClick={onDiagnose} disabled={loading} className="btn btn-outline btn-sm" style={{ flex: 1 }}>Diagnose Issue</button>
          <button onClick={onImprove} disabled={loading} className="btn btn-outline btn-sm" style={{ width: '100%' }}>{loading ? 'Improving...' : 'Improve Description'}</button>
        </div>
        {renderRealTimeSuggestion()}
        {data?.problem_summary && (
          <div className="ai-section animate-fade-in" style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 11, fontWeight: 800, color: 'var(--white-40)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Info size={14} style={{ color: 'var(--text-60a5fa)' }} /> Problem Summary
            </h4>
            <p style={{ fontSize: 15, color: 'var(--text-primary)', fontWeight: 700, lineHeight: 1.6, margin: 0 }}>{data.problem_summary}</p>
          </div>
        )}

        {data?.possible_causes?.length > 0 && (
          <div className="ai-section animate-fade-in" style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 11, fontWeight: 800, color: 'var(--white-40)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Bot size={14} style={{ color: 'var(--text-60a5fa)' }} /> Possible Causes
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.possible_causes.map((cause, i) => (
                <li key={i} style={{ fontSize: 14, color: 'var(--white-80)', display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--white-03)', padding: '12px 16px', borderRadius: '12px', border: '1px solid var(--white-05)', fontWeight: 600 }}>
                  <span style={{ color: 'var(--text-60a5fa)', flexShrink: 0 }}>•</span>
                  <span>{cause}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {data?.suggested_fix?.length > 0 && (
          <div className="ai-section animate-fade-in" style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 11, fontWeight: 800, color: 'var(--white-40)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={14} style={{ color: 'var(--text-3b82f6)' }} /> Recommended Action
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.suggested_fix.map((step, i) => (
                <div key={i} style={{ fontSize: 14, color: 'var(--white-80)', display: 'flex', gap: 12, alignItems: 'flex-start', background: 'rgba(16, 185, 129, 0.05)', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(16, 185, 129, 0.1)', fontWeight: 600 }}>
                  <span style={{ color: 'var(--text-3b82f6)', flexShrink: 0, fontWeight: 800 }}>{i + 1}</span>
                  <span style={{ lineHeight: 1.5 }}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data?.if_persists && (
          <div className="ai-section animate-fade-in" style={{ marginTop: 4, padding: '16px', background: 'rgba(245, 158, 11, 0.05)', borderRadius: '14px', border: '1px solid rgba(245, 158, 11, 0.1)' }}>
            <h4 style={{ fontSize: 11, fontWeight: 800, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lightbulb size={14} /> Expert Insights
            </h4>
            <p style={{ fontSize: 13, color: 'rgba(245, 158, 11, 0.8)', fontWeight: 600, margin: 0, lineHeight: 1.6 }}>{data.if_persists}</p>
          </div>
        )}

        {!loading && data && (
          <div className="ai-footer">
            <p className="mb-3 text-sm font-medium">Did this solve your issue?</p>
            <div className="flex gap-2">
              <button
                className="btn btn-success btn-sm flex-1"
                onClick={() => onResolve(true)}
              >
                Yes, Fixed!
              </button>
              <button
                className="btn btn-outline btn-sm flex-1"
                onClick={() => onResolve(false)}
              >
                No, Help me!
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
